import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService, RmsRequestResult } from './api.service';
import { strict } from 'assert';


export const USER_ROLES = ['Owner', 'Admin', 'Monitor', 'Sale', 'Accountant', 'Warehouse'];

export function roleInfo(value: string): { name: string, description: string, class: string } {
  switch (value) {
    case 'Owner': return {
      name: 'Chủ',
      description: 'Tài khoản có toàn quyền với hệ thống',
      class: 'text-danger',
    };

    case 'Admin': return {
      name: 'Quản lý',
      description: 'Tài khoản có quyền chỉnh sửa sản phẩm và giá nhập, chốt đơn đặt hàng xưởng',
      class: 'text-purple',
    };

    case 'Monitor': return {
      name: 'Giám sát',
      description: 'Tài khoản có quyền xem các báo cáo',
      class: 'text-warning',
    };

    case 'Sale': return {
      name: 'Bán hàng',
      description: 'Tài khoản có quyền tạo đơn đặt hàng cho khách',
      class: 'text-success',
    };

    case 'Accountant': return {
      name: 'Kế toán',
      description: 'Tài khoản có quyền tạo thu/chi',
      class: 'text-primary',
    }

    case 'Warehouse': return {
      name: 'Thủ kho',
      description: 'Tài khoản có quyền nhận hàng và xác nhận xuất nhập',
      class: 'text-info'
    }
  }
}

export function SKU2Parts(value: string): string[] {

  if (value.indexOf('-') > 0) return value.split('-');
  if (value.indexOf(':') > 0) return value.split(':');

  let test = /([0-9])([^0-9])/.exec(value);

  if (test) return [value.substr(0, test.index + 1).trim(), value.substr(test.index + 1).trim()];

  test = /([^0-9])([0-9])/.exec(value);

  if (test) return [value.substr(0, test.index + 1).trim(), value.substr(test.index + 1).trim()];

  return ['', value];
}

export function parts2SKU(producer: string, code: string): string {
  producer = producer.toUpperCase().trim();
  code = code.toUpperCase().trim();

  if (producer == '') return code;
  if (code.indexOf('(') > -1) return producer + ": " + code;

  let test1 = !isNaN(parseInt(producer.substr(producer.length - 1)));
  let test2 = !isNaN(parseInt(code.substr(0, 1)));  

  if ((test1 && !test2) || (!test1 && test2)) return producer + code;

  return producer + "-" + code;  
}

export function calculateExportPrice(product: ProductRecord, base: CurrencyRecord, quote: CurrencyRecord, adjustment: number): number {

  let price = product.UnitPrice > 0 ? (product.UnitPrice + product.LogisticsMargin + product.UnitPrice * product.ProfitMargin + adjustment) * base.Conversion / quote.Conversion : 0;
  return quote.RoundingFactor != 0 ? Math.ceil(price / quote.RoundingFactor) * quote.RoundingFactor : price;
}

export function orderState(order: OrderRecord): string {
  switch (order.State) {
    case OrderState.Editable:
      
      if (order.Details && order.Details.length > 0 && order.Details.count(i => !i.Verified || i.QuoteUnitPrice == 0) > 0) return "chờ kiểm giá";

      return 'chờ xác nhận';
      
    case OrderState.Confirmed:
      
      if (order.Completed) return 'hoàn thành';

      return order.Notified ? 'chờ hàng' : 'chờ báo xưởng';

    case OrderState.Canceled: return 'hủy';

    default: return 'lỗi';
  }
}

export function invoiceState(invoice: InvoiceRecord): string {
  switch (invoice.State) {
    case InvoiceState.Editable:
      return invoice.Type == InvoiceType.Import ? 'chờ hàng' : invoice.PartnerID < 3 ? 'chờ xử lý' : 'chờ xuất';

    case InvoiceState.Confirmed:

      return invoice.Type == InvoiceType.Export && invoice.PartnerID < 3 ? (invoice.PartnerID == 1 ? 'xưởng chịu' : 'công ty chịu') : 'hoàn thành';

    case InvoiceState.Canceled: return 'hủy';

    default: return 'lỗi';
  }
}

export function invoiceCategory(invoice: InvoiceRecord): string {
  switch (invoice.Category) {
    case InvoiceCategory.Qualified: return 'bình thường';
    case InvoiceCategory.Unqualified: return 'hàng lỗi';
    case InvoiceCategory.Lost: return 'hàng mất';
    default: return 'lỗi';
  }
}

export function discountState(discount: DiscountRecord): string {
  switch (discount.State) {
    case DiscountState.Editable: return 'chờ xử lý';

    case DiscountState.Confirmed: return 'hoàn thành';

    case DiscountState.Canceled: return 'hủy';

    default: return 'lỗi';
  }
}


@Injectable({
  providedIn: 'root'
})
export class DataService {

  constructor(private api: ApiService) {
  }  

  public partners(mode: 'discountable-list' | 'list' | 'create' | 'update', data: PartnerRecord = null): Observable<PartnerRecord[]> {
    return this.api.rms<any, PartnerRecord[]>("partners", { Mode: mode, Data: data }).pipe(map(response => response && response.Result == RmsRequestResult.OK ? response.Data : null));
  }

  public policies(mode: 'get' | 'update', data: PolicyRecord | { CustomerID: number } = null): Observable<PolicyRecord[]> {
    return this.api.rms<any, PolicyRecord[]>("policies", { Mode: mode, Data: data }).pipe(map(response => response && response.Result == RmsRequestResult.OK ? response.Data : null));
  }

  public products(mode: 'last-import-price' | 'list' | 'create' | 'update' | 'pending' | 'delete', data: ProductRecord = null): Observable<ProductRecord[]> {
    return this.api.rms<any, ProductRecord[]>("products", { Mode: mode, Data: data }).pipe(map(response => response && response.Result == RmsRequestResult.OK ? response.Data : null));
  }

  public orders(mode: 'incompleted' | 'completed' | 'notified' | 'unnotified' | 'create' | 'update' | 'confirm' | 'unconfirm' | 'cancel' | 'notify' | 'get', data: OrderRecord | { ID: number } | { Tag: string } = null): Observable<OrderRecord[]> {
    return this.api.rms<any, OrderRecord[]>("orders", { Mode: mode, Data: data }).pipe(map(response => response && response.Result == RmsRequestResult.OK ? response.Data : null));
  }

  public invoices(mode: 'imports' | 'exports' | 'create' | 'update' | 'confirm' | 'unconfirm' | 'cancel' | 'get', data: InvoiceRecord | { ID: number } | { Tag: string } = null): Observable<InvoiceRecord[]> {
    return this.api.rms<any, InvoiceRecord[]>("invoices", { Mode: mode, Data: data }).pipe(map(response => response && response.Result == RmsRequestResult.OK ? response.Data : null));
  }

  public tags(mode: 'list' | 'update', data: TagRecord = null): Observable<TagRecord[]> {
    return this.api.rms<any, TagRecord[]>("tags", { Mode: mode, Data: data }).pipe(map(response => response && response.Result == RmsRequestResult.OK ? response.Data : null));
  }

  public discounts(mode: 'pendings' | 'create' | 'update' | 'confirm' | 'cancel' | 'get', data: DiscountRecord | { ID: number } = null): Observable<DiscountRecord[]> {
    return this.api.rms<any, DiscountRecord[]>("discounts", { Mode: mode, Data: data }).pipe(map(response => response && response.Result == RmsRequestResult.OK ? response.Data : null));
  }

  public currencies(mode: 'list' | 'update', data: CurrencyRecord = null): Observable<CurrencyRecord[]> {
    return this.api.rms<any, CurrencyRecord[]>("currencies", { Mode: mode, Data: data }).pipe(map(response => response && response.Result == RmsRequestResult.OK ? response.Data : null));
  }

  public users(mode: 'list' | 'create' | 'update' | 'current', data: UserRecord = null): Observable<UserRecord[]> {
    return this.api.rms<any, UserRecord[]>("users", { Mode: mode, Data: data }).pipe(map(response => response && response.Result == RmsRequestResult.OK ? response.Data : null));
  }

  public goods(mode: 'remaining' | 'exportable' | 'customer-resolvable' | 'split', data: GoodRecord | { CustomerID: number } | { ID: number, ActualQuantity: number } = null): Observable<GoodRecord[]> {
    return this.api.rms<any, GoodRecord[]>("goods", { Mode: mode, Data: data }).pipe(map(response => response && response.Result == RmsRequestResult.OK ? response.Data : null));
  }

  public services(mode: 'list' | 'create' | 'update', data: ServiceRecord = null): Observable<ServiceRecord[]> {
    return this.api.rms<any, ServiceRecord[]>("services", { Mode: mode, Data: data }).pipe(map(response => response && response.Result == RmsRequestResult.OK ? response.Data : null));
  }

  public groups(mode: 'list' | 'create' | 'update', data: ServiceGroupRecord = null): Observable<ServiceGroupRecord[]> {
    return this.api.rms<any, ServiceGroupRecord[]>("groups", { Mode: mode, Data: data }).pipe(map(response => response && response.Result == RmsRequestResult.OK ? response.Data : null));
  }

  public debts(mode: 'customers' | 'get' | 'create' | 'delete', data: DebtRecord | { ID: number } = null): Observable<DebtRecord[]> {
    return this.api.rms<any, DebtRecord[]>("debts", { Mode: mode, Data: data }).pipe(map(response => response && response.Result == RmsRequestResult.OK ? response.Data : null));
  }

  public payments(mode: 'get' | 'services' | 'create' | 'update' | 'cancel' | 'confirm' | 'delete', data: PaymentRecord | { ID: number } = null): Observable<PaymentRecord[]> {
    return this.api.rms<any, PaymentRecord[]>("payments", { Mode: mode, Data: data }).pipe(map(response => response && response.Result == RmsRequestResult.OK ? response.Data : null));
  }

  public configs(mode: 'get' | 'update', data: Configs = null): Observable<Configs> {
    return this.api.rms<any, Configs>("configs", { Mode: mode, Data: data }).pipe(map(response => response && response.Result == RmsRequestResult.OK ? response.Data : null));
  }

  public reports(mode: 'general' | 'payments' | 'goods-by-products' | 'goods-by-customers' | 'goods-by-imports' | 'goods-by-exports' | 'goods-by-returns' | 'price-by-products', data: { range: DateRange, sku?: string, color?: string, customerId?: number, importInvoiceTag?: string }): Observable<ReportRecord> {
    return this.api.rms<any, ReportRecord>("reports", { Mode: mode, Range: [data.range.from.getTime(), data.range.to.getTime()], SKU: data.sku, Color: data.color, CustomerID: data.customerId, ImportInvoiceTag: data.importInvoiceTag }).pipe(map(response => response && response.Result == RmsRequestResult.OK ? response.Data : null));
  }
}

export interface ReportRecord {
  Goods: GoodReport[];
  Prices: PriceReport[];
  Payments: PaymentReport[];
  General: GeneralReport;
}

export interface GeneralReportRecord {
  Debt: number;
  Paid: number;
  Affection: 'income' | 'expense',
  Symbol: 'CNY' | 'VND'
}

export interface GeneralReport {

  ServiceIncomeValue: GeneralReportRecord;
  ServiceExpenseValue: GeneralReportRecord;

  VendorProductExportValue: GeneralReportRecord;
  VendorProductExportWorth: GeneralReportRecord;
  VendorProductReturnedValue: GeneralReportRecord;
  VendorProductReturnedWorth: GeneralReportRecord;
  VendorProductLostWorth: GeneralReportRecord;
  VendorProductDiscountWorth: GeneralReportRecord;
  
  CustomerProductExportValue: GeneralReportRecord;
  CustomerProductReturnedValue: GeneralReportRecord;    

  CustomerDebtIncreasedValue: GeneralReportRecord;
  CustomerDebtDecreasedValue: GeneralReportRecord;

  CustomerDiscountValue: GeneralReportRecord;
  CompanyLostValue: GeneralReportRecord;

  ExchangeRate: number;
}

export interface PaymentReport {
  ID: number;
  PartnerID: number;
  Type: PaymentType;
  Amount: number;
  Notes: string;
  Stamp: number;
}

export interface PriceReport {
  SKU: string;
  Color: string;
  Customer: string;
  ImportUnitPrice: number;
  ExportUnitPrice: number;
  Conversion: number;
  Stamp: number;
}

export interface GoodReportDetail {

  WeightCount: number;
  WeightQuantity: number;
  WeightQuoteQuantity: number;
  WeightDiscount: number;

  LengthCount: number;
  LengthQuantity: number;
  LengthQuoteQuantity: number;
  LengthDiscount: number;

  DiscountBaseWorth: number;
  DiscountQuoteWorth: number;
  DiscountQuoteValue: number;

  BaseWorth: number;
  QuoteWorth: number;
  QuoteValue: number;

  Unit: ProductUnit;
}

export interface GoodReport {
  Path: string[];
  Initial: GoodReportDetail;
  Imports: GoodReportDetail;
  Exports: GoodReportDetail;
  CompanyLosses: GoodReportDetail;
  ProviderLosses: GoodReportDetail;
  Returns: GoodReportDetail;
}

export interface Configs {
  CnyToVndConversion: number;
  VndRoundingFactor: number;
  InvoiceResolvableDays: number;
  GoodResolvableDays: number;
  MaxPrototypeLength: number;
  DefaultProfitMargin: number;
  DefaultLogisticsMargin: number;
}

export interface DateRange {
  from: Date;
  to: Date;
}

export interface CurrencyRecord {
  ID: number;
  Symbol: string;
  Icon: string;
  Conversion: number;
  RoundingFactor: number;
}

export interface PartnerRecord {
  ID: number;
  Name: string;
  Address: string;
  Phone: string;
  Email: string;
  DealerID: number;
  ImportPolicyID: number;
  ExportPolicyID: number;
}

export interface PolicyRecord {
  CustomerID: number;
  ProductID: number;
  Adjustment: number;
  Icon: string;
  Symbol: string;
}

export interface ProductRecord {
  ID: number;
  SKU: string;
  Unit?: ProductUnit;
  AverageQuantity: number;
  Colors: ProductColorRecord[];
  ProfitMargin: number;
  LogisticsMargin: number;
  UnitPrice: number;
  IsPriceVerified: boolean;
  Stamp?: number;
}

export interface ProductColorRecord {
  Color: string;
  Available: number;
}

export interface ServiceGroupRecord {
  ID: number;
  Name: string;
  Description: string;
}

export interface ServiceRecord {
  ID: number;
  GroupID: number;
  Name: string;
  Address: string;
}

export enum OrderState {
  Unknown = 0,
  Editable = 1,
  Confirmed = 2,
  Canceled = 3
}

export interface OrderRecord {
  ID: number;
  Tag: string;
  PartnerID: number;  
  ImportCurrency: string;
  QuoteCurrency: string;
  State: OrderState;
  FirstStamp: number;
  LastStamp: number;
  CreatorID: number;
  UpdatorID: number;
  Stamp: number;
  Completed: boolean;
  Notified: boolean;
  Details: OrderDetailRecord[];
}

export interface OrderDetailRecord {
  ID: number;
  OrderID: number;
  SKU: string;
  Color: string;
  Unit: ProductUnit;
  Quantity: number;
  ExpectedDelay: number;
  ExpectedUnitQuantity: number;
  Provided: number;
  ImportUnitPrice: number;
  QuoteUnitPrice: number;
  QuoteConversion: number;
  Verified: boolean;
}

export enum DiscountState {
  Unknown = 0,
  Editable = 1,
  Confirmed = 2,
  Canceled = 3
}

export interface DiscountRecord {
  ID: number;

  VendorID: number;
  CustomerID: number;
  
  State: DiscountState;

  FirstStamp: number;
  LastStamp: number;
  CreatorID: number;
  UpdatorID: number;

  ImportDiscountAmount: number;
  ImportDiscountIcon: string;
  ImportDiscountSymbol: string;

  ExportDiscountAmount: number;
  ExportDiscountIcon: string;
  ExportDiscountSymbol: string;

  Stamp: number;

  Details: DiscountDetailRecord[];
}

export interface DiscountDetailRecord {
  ID: number;
  SKU: string;
  Color: string;
  Unit: ProductUnit;

  ActualQuantity: number;
  ImportQuoteQuantity: number;
  ExportQuoteQuantity: number;
  ImportUnitPrice: number;  
  ExportUnitPrice: number;

  ImportDiscountQuantity: number;
  ExportDiscountQuantity: number;

  ImportInvoiceTag?: string;
  ExportInvoiceTag?: string;
}

export enum InvoiceType {
  Unknown = 0,
  Import = 1,
  Export = 2
}

export enum InvoiceState {
  Unknown = 0,
  Editable = 1,
  Confirmed = 2,
  Canceled = 3
}

export enum InvoiceCategory {
  Unknown = 0,
  Qualified = 1,
  Lost = 2,
  Unqualified = 3
}

export interface InvoiceRecord {
  ID: number;
  Tag: string;
  PartnerID: number;
  Type: InvoiceType;
  State: InvoiceState;
  Category: InvoiceCategory;
  FirstStamp: number;
  LastStamp: number;
  CreatorID: number;
  UpdatorID: number;
  SKUCount?: number;
  ColorCount?: number;
  LengthCount?: number;
  WeightCount?: number;
  LengthActualQuantity?: number;
  LengthQuoteQuantity?: number;
  WeightActualQuantity?: number;
  WeightQuoteQuantity?: number;
  Total?: number;
  Icon: string;
  Symbol: string;
  Stamp: number;

  Details: InvoiceDetailRecord[];
  Allocations: InvoiceAllocationRecord[];
}

export interface InvoiceDetailRecord {
  ID: number;
  SKU: string;
  Color: string;
  Unit?: ProductUnit;
  ImportInvoiceTag?: string;

  VendorID?: number;
  ImportOrderID: number;
  ImportOrderTag?: string;

  ExportInvoiceTag?: string;

  CustomerID?: number;
  ExportOrderID: number;
  ExportOrderTag?: string;    

  ActualQuantity: number;
  QuoteQuantity: number;
  DiscountQuantity?: number;
  QuoteUnitPrice: number;

  IsPriceEditable: boolean;
  IsReturned?: boolean;
}

export interface InvoiceAllocationRecord {
  OrderID: number;
  OrderTag?: string;
  CustomerID?: number;
  SKU: string;
  Color: string;
  QuoteUnitPrice: number;  
  Quantity: number;
}


export interface UserRecord {
  ID: number;
  Username: string;
  Password: string;
  Roles: string[];
}

export interface GoodRecord {
  ID: number;
  SKU: string;
  Color: string;
  Unit: ProductUnit;
  ActualQuantity: number;
  ImportQuoteQuantity: number;
  ExportQuoteQuantity: number;

  VendorID: number;

  ImportOrderID: number;
  ImportInvoiceID: number;
  ImportInvoiceTag?: string;
  ImportUnitPrice: number;
  ImportSymbol: string;
  ImportStamp: number;

  CustomerID: number;

  ExportOrderID: number;
  ExportInvoiceID: number;
  ExportDiscountID?: number;
  ExportUnitPrice: number;
  ExportSymbol: string;
  ExportStamp: number;

  DiscountID: number;

  ImportDiscountQuantity: number;
  ExportDiscountQuantity: number;

  ParentID: number;
  Children: GoodRecord[];
  Returned: boolean;  
}

export interface DebtRecord {
  ID: number;
  Name: string;
  Total: number;
  Icon: string;
  Symbol: string;
  Type: 'raised' | 'initial' | 'payment' | 'invoice' | 'discount' | 'adjustment';
  Stamp: number;
  Reason: string;
}

export enum PaymentType {
  Unknown = 0,
  Income = 1,
  Expense = 2,
  Partner = 4,
  Service = 8
}

export enum PaymentState {
  Unknown = 0,
  Editable = 1,
  Confirmed = 2,
  Canceled = 3
}

export enum ProductUnit {
  Unknown = 0,
  Meter = 1,
  Kilogram = 2
}

export interface PaymentRecord {
  ID: number;
  PartnerID: number;
  Type: PaymentType;
  State: PaymentState;
  SendAmount: number;
  SendSymbol: string;
  ReceiveAmount: number;
  ReceiveSymbol: string;
  Stamp: number;
  FirstStamp: number;
  LastStamp: number;
  CreatorID: number;
  UpdatorID: number;
  Notes: string;
}

export interface TagRecord {
  InvoiceID: number;
  PartnerID: number;
  Tag: string;
  Stamp: number;
}
